import React, { useEffect, useState } from "react";
import { DashboardLayout } from "@/components/DashboardLayout";
import { getNav } from "@/lib/navConfig";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Loader2, Bell, Mail, MessageSquare, Smartphone, Lock, Save } from "lucide-react";
import { toast } from "sonner";

const CHANNELS = [
  { k: "email", label: "Email", icon: Mail },
  { k: "sms", label: "SMS", icon: Smartphone },
  { k: "in_app", label: "In-App", icon: MessageSquare },
];

export default function NotificationPreferences() {
  const { user } = useAuth();
  const [events, setEvents] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/notification-preferences").then(({ data }) => setEvents(data.events)).catch(() => setEvents([]));
  useEffect(() => { load(); }, []);

  const toggle = (key, ch) => setEvents((prev) => prev.map((e) => e.key === key ? { ...e, channels: { ...e.channels, [ch]: !e.channels[ch] } } : e));

  const save = async () => {
    setSaving(true);
    try {
      const prefs = {};
      events.forEach((e) => { prefs[e.key] = { ...e.channels }; });
      await api.put("/notification-preferences", { prefs });
      toast.success("Notification preferences saved");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to save"); }
    setSaving(false);
  };

  const categories = events ? [...new Set(events.map((e) => e.category))] : [];

  return (
    <DashboardLayout nav={getNav(user.role, user.org_role)} title="Notification Preferences">
      {!events ? <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-blue-600" /></div> : (
        <div className="max-w-3xl space-y-6">
          <div className="flex items-start justify-between flex-wrap gap-3">
            <p className="text-sm text-slate-500 flex items-center gap-1.5"><Bell className="w-4 h-4 text-blue-600" /> Choose how you'd like to be notified. Required messages (billing, security, invitations) can't be turned off.</p>
            <Button data-testid="save-prefs-btn" onClick={save} disabled={saving} className="bg-blue-600 hover:bg-blue-700">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4 mr-1" /> Save Preferences</>}</Button>
          </div>

          {categories.map((cat) => (
            <div key={cat} className="bg-white border border-slate-200 rounded-xl overflow-hidden">
              <div className="px-5 py-3 bg-slate-50 border-b border-slate-200 flex items-center justify-between">
                <h3 className="font-head font-semibold text-slate-900">{cat}</h3>
                <div className="hidden sm:flex items-center gap-6 pr-1">
                  {CHANNELS.map((c) => <span key={c.k} className="text-xs font-semibold text-slate-400 w-12 text-center flex items-center justify-center gap-1"><c.icon className="w-3.5 h-3.5" />{c.label}</span>)}
                </div>
              </div>
              <div className="divide-y divide-slate-100">
                {events.filter((e) => e.category === cat).map((e) => (
                  <div key={e.key} data-testid={`pref-row-${e.key}`} className="px-5 py-3 flex items-center justify-between gap-4">
                    <div className="min-w-0">
                      <div className="text-sm font-medium text-slate-800 flex items-center gap-2">{e.label}{e.required && <span className="text-[10px] font-bold uppercase text-amber-700 bg-amber-50 px-1.5 py-0.5 rounded flex items-center gap-1"><Lock className="w-2.5 h-2.5" />Required</span>}</div>
                    </div>
                    <div className="flex items-center gap-6 shrink-0">
                      {CHANNELS.map((c) => {
                        const locked = e.locked.includes(c.k);
                        return (
                          <div key={c.k} className="w-12 flex justify-center">
                            <Switch data-testid={`pref-${e.key}-${c.k}`} checked={!!e.channels[c.k]} disabled={locked} onCheckedChange={() => !locked && toggle(e.key, c.k)} />
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </DashboardLayout>
  );
}
